import { Form, FormInstance } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import { useChatSessionDetail, useLazyCallSessionDetail, useLazyChatSessionDetail, useSearchParamsForm } from 'hooks';
import { get, set } from 'lodash';
import React, { createContext, useEffect, useRef, useState } from 'react';
import { useContext } from 'react';
import {
  joinCallSessionAiProcessing,
  leaveCallSessionAiProcessing,
  leaveChatSessionMonitoring,
  startChatMonitoring
} from 'services';
import { CallSessionDto, ChatSessionDto } from 'types';
import { CALL_SOURCE, CALL_SOURCE_OPTION, CHAT_SESSION_SOURCE } from 'utils';

export type OmniHistoriesLeadManageContextProps = {
  tab: 'ongoing' | 'history';
  setTab: (tab: 'ongoing' | 'history') => void;

  type?: 'phone' | 'chat';
  setType: (type: 'phone' | 'chat') => void;

  callSession?: CallSessionDto | null;
  setCallSession: (session: CallSessionDto | null) => void;

  chatSession?: ChatSessionDto | null;
  setChatSession: (session: ChatSessionDto | null) => void;

  form: FormInstance<FilterForm>;

  filterForm?: Pick<
    FilterForm,
    'callSource' | 'chatSource' | 'dateRange' | 'callDateRange' | 'keywordChat' | 'keywordCall'
  >;
};

export const AIAgentCenterContext = createContext<OmniHistoriesLeadManageContextProps | undefined>(undefined);

type FilterForm = {
  tab: 'ongoing' | 'history';
  type: 'phone' | 'chat';

  chatSource?: CHAT_SESSION_SOURCE;
  callSource?: CALL_SOURCE;

  dateRange?: [string | Dayjs, string | Dayjs] | null;
  callDateRange?: [string | Dayjs, string | Dayjs] | null;

  keywordChat?: string;
  keywordCall?: string;
};

export const AIAgentCenter: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const { setSearchParams, getSearchParams, setSearchParam } = useSearchParamsForm();

  const [form] = Form.useForm<FilterForm>();
  const { tab, type, callSource, chatSource, dateRange, callDateRange, keywordChat, keywordCall } =
    Form.useWatch([], form) || getSearchParams();

  const [callSession, setCallSession] = useState<CallSessionDto | null>(null);
  const [chatSession, setChatSession] = useState<ChatSessionDto | null>(null);
  const { loadChatSessionDetail, data: chatSessionDetailData } = useLazyChatSessionDetail();
  const { loadCallSessionDetail, data: callSessionDetailData } = useLazyCallSessionDetail();

  const prevSessionIdRef = useRef<number | null>(null);

  useEffect(() => {
    const newSessionId = chatSession?.chatSessionId;
    const prevSessionId = prevSessionIdRef.current;

    if (prevSessionId && !newSessionId) {
      leaveChatSessionMonitoring(prevSessionId);
    }

    if (newSessionId && newSessionId !== prevSessionId) {
      startChatMonitoring(newSessionId);
    }

    prevSessionIdRef.current = newSessionId ?? null;
  }, [chatSession?.chatSessionId]);

  useEffect(() => {
    const callSessionId = callSession?.callSessionId;

    if (callSessionId) {
      joinCallSessionAiProcessing(callSessionId);
    }

    return () => {
      if (callSessionId) {
        leaveCallSessionAiProcessing(callSessionId);
      }
    };
  }, [callSession?.callSessionId]);

  const handleSetTab = (tab: 'ongoing' | 'history') => {
    form.setFieldsValue({
      tab,
      callSource: undefined,
      chatSource: undefined,
      dateRange: [dayjs().startOf('day'), dayjs().endOf('day')],
      callDateRange: [dayjs().startOf('day'), dayjs().endOf('day')]
    });

    const values = form.getFieldsValue();
    setSearchParams({
      ...values,
      dateRange: [dayjs().format('YYYY-MM-DD'), dayjs().format('YYYY-MM-DD')],
      callDateRange: [dayjs().format('YYYY-MM-DD'), dayjs().format('YYYY-MM-DD')]
    });
    setCallSession(null);
    setChatSession(null);
  };

  const handleSetType = (type: 'phone' | 'chat') => {
    form.setFieldValue('type', type);
    setSearchParam('type', type);
    setCallSession(null);
    setChatSession(null);
  };

  const handleSelectChatSession = async (session: ChatSessionDto | null) => {
    if (chatSessionDetailData?.chatSessionId !== session?.chatSessionId) {
      const detail = await loadChatSessionDetail(session?.chatSessionId || 0);
      if (detail) {
        setChatSession({
          ...session,
          ...detail
        });
      }
    } else {
      setChatSession((prev) => (prev && session ? { ...prev, ...session } : session));
    }
  };

  const handleSelectCallSession = async (session: CallSessionDto | null) => {
    if (callSessionDetailData?.callSessionId !== session?.callSessionId) {
      const detail = await loadCallSessionDetail(session?.callSessionId || 0);
      if (detail) {
        setCallSession({
          ...session,
          ...detail
        });
      }
    } else {
      setCallSession((prev) => (prev && session ? { ...prev, ...session } : session));
    }
  };

  return (
    <AIAgentCenterContext.Provider
      value={{
        tab,
        setTab: handleSetTab,

        type,
        setType: handleSetType,

        callSession,
        setCallSession: handleSelectCallSession,

        chatSession,
        setChatSession: handleSelectChatSession,

        form: form,

        filterForm: {
          callSource,
          chatSource,
          dateRange,
          callDateRange,
          keywordCall,
          keywordChat
        }
      }}
    >
      {/* <Spin className='max-h-full overflow-hidden' spinning={isLoading || isFetching}> */}
      <Form
        form={form}
        initialValues={{
          tab: 'ongoing',
          type: 'phone',
          ...getSearchParams(),
          dateRange: getSearchParams().dateRange
            ? [dayjs(getSearchParams().dateRange[0]), dayjs(getSearchParams().dateRange[1])]
            : [dayjs().startOf('day'), dayjs().endOf('day')],
          callDateRange: getSearchParams().callDateRange
            ? [dayjs(getSearchParams().callDateRange[0]), dayjs(getSearchParams().callDateRange[1])]
            : [dayjs().startOf('day'), dayjs().endOf('day')]
        }}
        onValuesChange={() => {
          const { dateRange, callDateRange, ...values } = form.getFieldsValue();
          const dateRangeFormatted = dateRange
            ? [dayjs(dateRange[0]).format('YYYY-MM-DD'), dayjs(dateRange[1]).format('YYYY-MM-DD')]
            : undefined;
          const callDateRangeFormatted = callDateRange
            ? [dayjs(callDateRange[0]).format('YYYY-MM-DD'), dayjs(callDateRange[1]).format('YYYY-MM-DD')]
            : undefined;

          setSearchParams({ ...values, dateRange: dateRangeFormatted, callDateRange: callDateRangeFormatted });
        }}
      >
        <Form.Item name='tab' hidden noStyle />
        <Form.Item name='type' hidden noStyle />
        <Form.Item name='chatSource' hidden noStyle />
        <Form.Item name='callSource' hidden noStyle />
        <Form.Item name='dateRange' hidden noStyle />
        <Form.Item name='callDateRange' hidden noStyle />
        <Form.Item name='keywordCall' hidden noStyle />
        <Form.Item name='keywordChat' hidden noStyle />

        {children}
      </Form>
      {/* </Spin> */}
    </AIAgentCenterContext.Provider>
  );
};
export default AIAgentCenter;

export const useAIAgentCenterContext = (): OmniHistoriesLeadManageContextProps => {
  const context = useContext(AIAgentCenterContext);
  if (!context) {
    throw new Error('useAIAgentCenterContext must be used within a OmniHistoriesProvider');
  }
  return context;
};
